/* components/versioning/DiffView.jsx — сравнение двух снимков (было → стало).
   Скалярные поля: список изменений (старое зачёркнуто красным → новое зелёным).
   Составные разделы (состав ТТК / блюда меню): две колонки «Было | Стало» рядом
   (xl/lg), на xs/sm — Segmented-переключатель. Строки: добавлено/удалено/изменено.
   Язык diff единый с ConfirmActionCard чата. props-only (без импорта моков).
   ХЕНДОФФ → frontend/src/components/versioning/DiffView.jsx. */

import React from 'react';
import { Segmented } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';

  const { useState } = React;

  const getPath = (o, path) => path.split('.').reduce((a, k) => (a == null ? a : a[k]), o);

  // конфиг полей по kind
  const CFG = {
    card: {
      scalars: [
        { key: 'name', label: 'Название' },
        { key: 'category', label: 'Категория' },
        { key: 'default_unit', label: 'Единица учёта' },
        { key: 'kcal_per_100g', label: 'Ккал/100 г', kind: 'num' },
        { key: 'protein_per_100g', label: 'Белки, г', kind: 'num' },
        { key: 'fat_per_100g', label: 'Жиры, г', kind: 'num' },
        { key: 'carbs_per_100g', label: 'Углеводы, г', kind: 'num' },
        { key: 'current_price.amount_rub', label: 'Цена закупки', kind: 'money' },
        { key: 'allergens', label: 'Аллергены', kind: 'list' },
      ],
      composite: null,
    },
    ttk: {
      scalars: [
        { key: 'title', label: 'Название' },
        { key: 'output_g', label: 'Выход порции', kind: 'grams' },
        { key: 'storage_conditions', label: 'Хранение' },
        { key: 'allergens', label: 'Аллергены', kind: 'list' },
      ],
      composite: { field: 'ingredient_rows', title: 'Состав', keyOf: (r) => r.target_label || r.name_raw,
        sub: [{ key: 'amount_brutto_g', label: 'Брутто', kind: 'grams' }, { key: 'amount_netto_g', label: 'Нетто', kind: 'grams' }] },
    },
    menu: {
      scalars: [
        { key: 'name', label: 'Название' },
        { key: 'average_check', label: 'Средний чек', kind: 'money' },
        { key: 'target_food_cost_pct', label: 'Целевой food cost', kind: 'pct' },
      ],
      composite: { field: 'dishes', title: 'Блюда', keyOf: (d) => d.title,
        sub: [{ key: 'section', label: 'Раздел' }, { key: 'food_cost_pct', label: 'FC', kind: 'pct' }] },
    },
  };

  function fval(v, kind) {
    if (v == null || v === '') return '—';
    if (kind === 'money') return fmt.money(v);
    if (kind === 'pct') return fmt.percent(v, { digits: 0 });
    if (kind === 'grams') return fmt.grams(v);
    if (kind === 'num') return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);
    if (kind === 'list') return (Array.isArray(v) && v.length) ? v.join(', ') : '—';
    if (kind === 'bool') return v ? 'да' : 'нет';
    return String(v);
  }
  const same = (a, b, kind) => {
    if (kind === 'list') return (Array.isArray(a) ? a : []).join('|') === (Array.isArray(b) ? b : []).join('|');
    return (a == null ? '' : a) === (b == null ? '' : b);
  };

  function buildDiff(kind, a, b) {
    const cfg = CFG[kind] || CFG.card;
    const scalars = cfg.scalars.map((f) => {
      const av = getPath(a, f.key), bv = getPath(b, f.key);
      return { ...f, a: av, b: bv, changed: !same(av, bv, f.kind) };
    }).filter((s) => s.changed);

    let rows = [];
    if (cfg.composite) {
      const A = (getPath(a, cfg.composite.field) || []).filter((x) => !x._deleted);
      const B = (getPath(b, cfg.composite.field) || []);
      const keyOf = cfg.composite.keyOf;
      const aMap = new Map(A.map((x) => [keyOf(x), x]));
      const bMap = new Map(B.map((x) => [keyOf(x), x]));
      const keys = [];
      A.forEach((x) => keys.push(keyOf(x)));
      B.forEach((x) => { if (!aMap.has(keyOf(x))) keys.push(keyOf(x)); });
      rows = keys.map((k) => {
        const ar = aMap.get(k), br = bMap.get(k);
        let status = 'same';
        if (ar && !br) status = 'removed';
        else if (!ar && br) status = 'added';
        else {
          const diffSub = cfg.composite.sub.some((s) => !same(ar[s.key], br[s.key], s.kind));
          status = diffSub ? 'changed' : 'same';
        }
        return { key: k, status, a: ar, b: br, sub: cfg.composite.sub };
      });
    }
    const compositeChanged = rows.some((r) => r.status !== 'same');
    return { scalars, rows, compositeTitle: cfg.composite && cfg.composite.title, identical: scalars.length === 0 && !compositeChanged };
  }

  // одна сторона составного раздела (было=a / стало=b)
  function CompositeSide({ side, rows }) {
    return (
      <ul className="diff-rows">
        {rows.map((r) => {
          const item = side === 'a' ? r.a : r.b;
          if (!item) {
            // отсутствует на этой стороне
            const isGap = (side === 'a' && r.status === 'added') || (side === 'b' && r.status === 'removed');
            return <li key={r.key} className="diff-row diff-row--gap" aria-hidden="true">{isGap ? '' : ''}</li>;
          }
          const cls = side === 'a'
            ? (r.status === 'removed' ? 'is-removed' : r.status === 'changed' ? 'is-changed' : '')
            : (r.status === 'added' ? 'is-added' : r.status === 'changed' ? 'is-changed' : '');
          return (
            <li key={r.key} className={'diff-row ' + cls}>
              <span className="diff-row__name truncate">{r.key}
                {side === 'b' && r.status === 'added' && <span className="badge badge--ok badge--sm">добавлено</span>}
                {side === 'a' && r.status === 'removed' && <span className="badge badge--bad badge--sm">удалено</span>}
              </span>
              <span className="diff-row__meta">
                {r.sub.map((s) => <span key={s.key} className="diff-row__sub">{s.label}: <span className="num">{fval(item[s.key], s.kind)}</span></span>)}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  function DiffView({ kind, a, b, labelA = 'Было', labelB = 'Стало' }) {
    const { isMobile } = hooks.useBreakpoint();
    const [side, setSide] = useState('b'); // мобильный переключатель
    const diff = buildDiff(kind, a || {}, b || {});

    if (diff.identical) {
      return (
        <div className="diff-identical">
          <Icon name="check-circle" size={28} />
          <p className="t-body">Версии идентичны — отличий не найдено.</p>
        </div>
      );
    }

    return (
      <div className="diff">
        {diff.scalars.length > 0 && (
          <section className="diff-sec">
            <h4 className="diff-sec__title t-caption">Поля</h4>
            <ul className="diff-scalars">
              {diff.scalars.map((s) => (
                <li key={s.key} className="diff-scalar">
                  <span className="diff-scalar__label">{s.label}</span>
                  <span className="diff-scalar__change">
                    <span className="diff-old">{fval(s.a, s.kind)}</span>
                    <Icon name="arrow-right" size={13} className="diff-scalar__arrow" />
                    <span className="diff-new">{fval(s.b, s.kind)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {diff.compositeTitle && diff.rows.length > 0 && (
          <section className="diff-sec">
            <div className="diff-sec__head">
              <h4 className="diff-sec__title t-caption">{diff.compositeTitle}</h4>
              {isMobile && (
                <Segmented size="sm" compact value={side} onChange={setSide}
                  options={[{ value: 'a', label: labelA }, { value: 'b', label: labelB }]} ariaLabel="Сторона сравнения" />
              )}
            </div>
            {isMobile ? (
              <div className="diff-col">
                <div className="diff-col__head t-caption">{side === 'a' ? labelA : labelB}</div>
                <CompositeSide side={side} rows={diff.rows} />
              </div>
            ) : (
              <div className="diff-cols">
                <div className="diff-col">
                  <div className="diff-col__head t-caption">{labelA}</div>
                  <CompositeSide side="a" rows={diff.rows} />
                </div>
                <div className="diff-col">
                  <div className="diff-col__head t-caption">{labelB}</div>
                  <CompositeSide side="b" rows={diff.rows} />
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    );
  }

export { DiffView };
export { buildDiff };
